import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import GlobalDeleteModal from "../../../components/partials/GlobalDeleteModal";
import CouponAPI from "../../../api/promo/couponApi";
import TableShimmer from "../../../components/partials/TableShimmer";
import CouponRow from "./CouponRow";
import RowsPerPage from "../../../components/partials/RowsPerPage";
import Pagination from "../../../components/partials/Pagination";
import { showError, showSuccess } from "../../../utils/toastUtils";

function List() {
  const [tableLoading, setTableLoading] = useState(false);

  const [couponData, setCouponData] = useState([]); // State for store list
  const [pagination, setPagination] = useState({}); // State for pagination info
  const [searchParams, setSearchParams] = useSearchParams();

  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const query = React.useMemo(
    () => ({
      page: Number(searchParams.get("page")) || 1,
      limit: Number(searchParams.get("limit")) || 10,
      status: searchParams.get("status") || null,
      search: searchParams.get("search") || null,
    }),
    [searchParams],
  );

  const fetchCouponData = useCallback(async () => {
    setTableLoading(true);
    setCouponData([]);
    setPagination({});

    try {
      const res = await CouponAPI.getList(searchParams);
      if (res.code === 200) {
        const { pagination, coupons } = res.data;

        setCouponData(coupons ?? []);
        setPagination(pagination ?? {});
      }
    } catch {
    } finally {
      setTableLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchCouponData();
  }, [fetchCouponData]);

  const searchTimeout = useRef();
  const handleSearch = (value) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateFilter("search", value);
    }, 400);
  };

  const updateFilter = useCallback(
    (key, value) => {
      const params = new URLSearchParams(searchParams);
      value ? params.set(key, value) : params.delete(key);

      if(key !== "page") {
        params.set("page", 1);
      }
      
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleStatusChange = async (storeId, status) => {
    try {
      const res = await CouponAPI.updateStatus(storeId);

      if (res.code === 200) {
        setCouponData((prev) =>
          prev.map((store) =>
            store._id === storeId ? { ...store, status: status } : store,
          ),
        );

        showSuccess("Status updated successfully");
      } else {
        showError("Failed to update status");
      }
    } catch (err) {
      showError("Something went wrong");
    } finally {
    }
  };



  const handleVisibilityChange = async (storeId, isVisible) => {
    try {
      const res = await CouponAPI.visibilityStatus(storeId);

      if (res.code === 200) {
        setCouponData((prev) =>
          prev.map((store) =>
            store._id === storeId ? { ...store, isVisible: isVisible } : store,
          ),
        );

        showSuccess("Visibility updated successfully");
      } else {
        showError("Failed to update status");
      }
    } catch (err) {
      showError("Something went wrong");
    } finally {
    }
  };




  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      setIsDeleting(true);

      const res = await CouponAPI.deleteItem(deleteId);

      if (res.code === 200) {
        showSuccess(res.message);
        fetchCouponData();

        document.querySelector("#delete-modal .btn-close").click();
      } else {
        showError(res.message);
      }
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Manage Coupons</h4>
              <h6>
                View, search, and manage all discount coupons available in your
                store.
              </h6>
            </div>
          </div>
          <div className="page-btn">
            <Link to="/coupon/add-new" className="btn btn-primary">
              <i className="ti ti-circle-plus me-1" />
              Add Coupon
            </Link>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="row">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text" id="basic-addon1">
                    <i className="ti ti-search" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search By Coupon Code"
                    defaultValue={query.search}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-6" style={{ justifyItems: "right" }}>
                <div className="d-flex table-dropdown my-xl-auto right-content align-items-center row-gap-3">
                  <select
                    className="form-control ms-2"
                    value={searchParams.get("status") || ""}
                    onChange={(e) => updateFilter("status", e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            {pagination.page && (
              <div className="row">
                <div className="col-md-12">
                  <div className="countablepagination p-2">
                    <p>
                      Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                      {Math.min(
                        pagination.page * pagination.limit,
                        pagination.total,
                      )}{" "}
                      of {pagination.total} results
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="table-responsive">
              <table className="table">
                <thead className="thead-light">
                  <tr>
                    <th>S. No.</th>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Max. Discount Value</th>
                    <th>Visibility</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    <TableShimmer rows={10} cols={8} />
                  ) : couponData.length > 0 ? (
                    couponData.map((coupon, index) => (
                      <CouponRow
                        key={coupon._id}
                        coupon={coupon}
                        index={index}
                        page={query.page}
                        limit={query.limit}
                        onStatusChange={handleStatusChange}
                        onVisibilityChange={handleVisibilityChange}
                        onDelete={handleOpenDeleteModal}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center p-4 text-muted">
                        No Coupon Added,{" "}
                        <Link to="/coupon/add-new" className="text-primary">
                          Click Here{" "}
                        </Link>
                        to add new coupon
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {pagination.page && (
              <div className="row">
                <RowsPerPage
                  value={searchParams.get("limit") || "10"}
                  onChange={(val) => updateFilter("limit", val)}
                />

                <Pagination
                  pagination={pagination}
                  updateFilter={updateFilter}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <GlobalDeleteModal onDeleted={handleDelete} isDeleting={isDeleting} />
    </>
  );
}

export default List;
